// src/hooks/useSystemHealth.js
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Periodically probes GET /api/health and reports the connectivity of the
 * three tiers the status indicator cares about:
 *   - frontend: always "up" (this code is running, so the SPA is served)
 *   - backend:  "up" if the request resolves, "down" on network error/timeout
 *   - database: read from the health payload's components.database
 *
 * Derived overall state:
 *   - "online"   every tier healthy
 *   - "degraded" backend reachable but the DB is down
 *   - "offline"  backend unreachable
 *   - "checking" before the first probe resolves
 *
 * Returns the health state plus `isChecking` (a probe is in flight) and
 * `refresh()` for manual re-checks.
 */
const POLL_MS = 30_000;
const REQUEST_TIMEOUT_MS = 8_000;

export default function useSystemHealth(pollMs = POLL_MS) {
  const [health, setHealth] = useState({
    overall: "checking",
    components: { frontend: "up", backend: "unknown", database: "unknown" },
    lastChecked: null,
  });
  const [isChecking, setIsChecking] = useState(false);
  const timerRef = useRef(null);

  const check = useCallback(async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    setIsChecking(true);

    try {
      const res = await fetch("/api/health", {
        signal: controller.signal,
        headers: { Accept: "application/json" },
        cache: "no-store",
      });

      let body = null;
      try {
        body = await res.json();
      } catch {
        /* non-JSON response — treat DB state as unknown below */
      }

      const dbUp = body?.components?.database === "ok";

      setHealth({
        overall: dbUp ? "online" : "degraded",
        components: {
          frontend: "up",
          backend: "up",
          database: dbUp ? "up" : "down",
        },
        lastChecked: new Date(),
      });
    } catch {
      // Network error, timeout, or abort — the API is unreachable.
      setHealth({
        overall: "offline",
        components: { frontend: "up", backend: "down", database: "unknown" },
        lastChecked: new Date(),
      });
    } finally {
      clearTimeout(timeout);
      setIsChecking(false);
    }
  }, []);

  useEffect(() => {
    check();
    timerRef.current = setInterval(check, pollMs);

    // Re-probe immediately when the user returns to the tab so a stale
    // "offline" doesn't linger after the network comes back.
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [check, pollMs]);

  return { ...health, isChecking, refresh: check };
}
